import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthentication } from "./auth/AuthContext";
import { WorldPage } from "./WorldPage";

type IconName =
  "command" | "nation" | "economy" | "military" | "diplomacy" | "history";

const domains: ReadonlyArray<{ label: string; icon: IconName }> = [
  { label: "Command", icon: "command" },
  { label: "Nation", icon: "nation" },
  { label: "Economy", icon: "economy" },
  { label: "Military", icon: "military" },
  { label: "Diplomacy", icon: "diplomacy" },
  { label: "History", icon: "history" },
];

const commands = [
  { name: "Open command overview", group: "Navigate", shortcut: "G C" },
  { name: "Review world briefing", group: "Briefing", shortcut: "G B" },
  { name: "Show strategic region index", group: "Map", shortcut: "G R" },
  { name: "Change performance mode", group: "Settings", shortcut: "G P" },
];

function Glyph({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    command: (
      <>
        <circle cx="12" cy="12" r="7" />
        <path d="m12 5 2 5 5 2-5 2-2 5-2-5-5-2 5-2Z" />
      </>
    ),
    nation: (
      <>
        <path d="M5 20V7l7-3 7 3v13" />
        <path d="M3 20h18M9 10h6M9 14h6" />
      </>
    ),
    economy: (
      <>
        <path d="M4 19V9m5 10V5m6 14v-7m5 7V3" />
        <path d="M2 21h20" />
      </>
    ),
    military: (
      <>
        <path d="M12 3v18M5 6l14 12M19 6 5 18" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    diplomacy: (
      <>
        <path d="M7 12h10M12 7v10" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
    history: (
      <>
        <path d="M4 12a8 8 0 1 0 2-5.3L4 9" />
        <path d="M4 4v5h5M12 7v5l3 2" />
      </>
    ),
  };
  return (
    <svg className="glyph" aria-hidden="true" viewBox="0 0 24 24">
      {paths[name]}
    </svg>
  );
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(
    () =>
      commands.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase()),
      ),
    [query],
  );

  useEffect(() => {
    inputRef.current?.focus();
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div
      className="scrim"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-labelledby="palette-title"
      >
        <div className="palette-search">
          <span aria-hidden="true">⌕</span>
          <label
            id="palette-title"
            className="sr-only"
            htmlFor="command-search"
          >
            Command palette
          </label>
          <input
            ref={inputRef}
            id="command-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search commands and destinations…"
          />
          <kbd>Esc</kbd>
        </div>
        <p className="section-label">Available in foundation mode</p>
        <ul className="command-results">
          {filtered.map((item, index) => (
            <li key={item.name}>
              <button
                type="button"
                onClick={onClose}
                className={index === 0 ? "highlighted" : ""}
              >
                <span>
                  <small>{item.group}</small>
                  {item.name}
                </span>
                <kbd>{item.shortcut}</kbd>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="empty-result">No commands match “{query}”.</li>
          )}
        </ul>
        <footer>
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>Current universe only</span>
        </footer>
      </section>
    </div>
  );
}

export function FoundationPage() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [activeDomain, setActiveDomain] = useState("Command");
  const paletteButton = useRef<HTMLButtonElement>(null);
  const authentication = useAuthentication();
  const accountName =
    authentication.session?.displayName ??
    (authentication.status === "authenticated" ? "Player" : "Sign in");
  const accountInitials =
    authentication.session?.displayName
      ?.split(" ")
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() ?? "ID";

  useEffect(() => {
    const open = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", open);
    return () => window.removeEventListener("keydown", open);
  }, []);

  const closePalette = () => {
    setPaletteOpen(false);
    window.setTimeout(() => paletteButton.current?.focus(), 0);
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#strategic-view">
        Skip to strategic view
      </a>
      <header className="topbar">
        <a
          className="wordmark"
          href="/foundation"
          aria-label="PlanetConflict command overview"
        >
          <span className="brand-mark" aria-hidden="true">
            PC
          </span>
          <span>
            PLANET<em>CONFLICT</em>
          </span>
        </a>
        <div className="universe-state" aria-label="Universe status">
          <span className="status-pulse" aria-hidden="true" />
          Universe foundation <b>ONLINE</b>
        </div>
        <button
          ref={paletteButton}
          className="command-trigger"
          type="button"
          onClick={() => setPaletteOpen(true)}
        >
          <span aria-hidden="true">⌕</span>
          <span>Search or issue a command</span>
          <kbd>Ctrl K</kbd>
        </button>
        <div className="top-actions">
          <time dateTime="2026-07-12T19:35:00Z">
            <span>UNIVERSE TIME</span>19:35 UTC
          </time>
          <button
            className="icon-button notice-button"
            type="button"
            aria-label="Open notifications, 2 notices"
            onClick={() => setNoticesOpen(true)}
          >
            ♢<span>2</span>
          </button>
          <button
            className="profile"
            type="button"
            aria-label={
              authentication.status === "authenticated"
                ? "Open account menu"
                : "Sign in to PlanetConflict"
            }
            onClick={() => setAccountOpen(true)}
          >
            <span>{accountName}</span>
            <b>{accountInitials}</b>
          </button>
        </div>
      </header>

      <aside className="rail">
        <nav aria-label="Strategic domains">
          {domains.map((item) => (
            <button
              key={item.label}
              type="button"
              className={activeDomain === item.label ? "active" : ""}
              aria-current={activeDomain === item.label ? "page" : undefined}
              onClick={() => setActiveDomain(item.label)}
            >
              <Glyph name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="rail-footer">
          <button type="button" aria-label="Settings">
            ⚙
          </button>
          <a
            href="https://planetconflict.com"
            aria-label="PlanetConflict public site"
          >
            ↗
          </a>
        </div>
      </aside>

      <main id="strategic-view" className="strategic-view" tabIndex={-1}>
        <div className="map-toolbar" aria-label="Strategic view controls">
          <div>
            <span className="section-label">STRATEGIC VIEW</span>
            <strong>Origin · Survey frame 01</strong>
          </div>
          <div className="segmented" aria-label="Map presentation">
            <button className="selected" type="button" aria-pressed="true">
              Strategic
            </button>
            <button
              type="button"
              disabled
              title="Available with the planet renderer"
            >
              Orbital
            </button>
          </div>
        </div>
        <section className="world-stage" aria-labelledby="world-title">
          <div className="stars" aria-hidden="true" />
          <div className="planet" aria-hidden="true">
            <span className="scan-line" />
            <i className="marker marker-one" />
            <i className="marker marker-two" />
            <i className="marker marker-three" />
          </div>
          <div className="view-copy">
            <p className="eyebrow">PERMANENT UNIVERSE · PRE-COLONIZATION</p>
            <h1 id="world-title">
              A world awaiting
              <br />
              <span>its first nations.</span>
            </h1>
            <p>
              The command network is operational. Authoritative world generation
              has not begun, so no territory, resources, or player activity are
              being fabricated.
            </p>
            <button
              className="primary-action"
              type="button"
              onClick={() => setBriefingOpen(true)}
            >
              Read foundation briefing <span aria-hidden="true">→</span>
            </button>
          </div>
          <div className="map-legend">
            <span>
              <i /> Network online
            </span>
            <span>
              <i /> Data unavailable
            </span>
            <b>LOW-GPU STRATEGIC MODE</b>
          </div>
        </section>
        <section className="region-index" aria-labelledby="region-title">
          <div>
            <span className="section-label">NON-VISUAL ALTERNATIVE</span>
            <h2 id="region-title">Strategic region index</h2>
          </div>
          <p>
            No regions exist yet. Once the authoritative world is generated,
            this searchable table will provide the same critical actions as the
            planet view.
          </p>
        </section>
      </main>

      <aside
        className={`briefing-panel ${briefingOpen ? "panel-open" : ""}`}
        aria-label="World briefing"
      >
        <div className="panel-heading">
          <div>
            <span className="section-label">WORLD BRIEFING</span>
            <h2>Foundation status</h2>
          </div>
          <button
            type="button"
            aria-label="Close briefing"
            onClick={() => setBriefingOpen(false)}
          >
            ×
          </button>
        </div>
        <p className="briefing-intro">
          The platform is online. Gameplay systems remain deliberately
          unavailable until their authoritative rules and contracts are
          implemented.
        </p>
        <ol className="readiness-list">
          <li className="complete">
            <span>01</span>
            <div>
              <b>Command network</b>
              <small>API, simulation host, and deployment path</small>
            </div>
            <strong>READY</strong>
          </li>
          <li className="current">
            <span>02</span>
            <div>
              <b>Application shell</b>
              <small>Desktop, mobile, and accessible controls</small>
            </div>
            <strong>ACTIVE</strong>
          </li>
          <li>
            <span>03</span>
            <div>
              <b>Planetary survey</b>
              <small>Seeded world and strategic topology</small>
            </div>
            <strong>PENDING</strong>
          </li>
          <li>
            <span>04</span>
            <div>
              <b>Nation founding</b>
              <small>Identity and permanent-world entry</small>
            </div>
            <strong>PENDING</strong>
          </li>
        </ol>
        <div className="status-card">
          <span aria-hidden="true">◇</span>
          <div>
            <b>No orders pending</b>
            <p>The command queue is empty because no playable nation exists.</p>
          </div>
        </div>
        <button
          className="secondary-action"
          type="button"
          onClick={() => setPaletteOpen(true)}
        >
          Explore available commands <span>Ctrl K</span>
        </button>
        <p className="stale-note">
          Briefing snapshot · 12 Jul 2026 · Refreshes on deployment
        </p>
      </aside>

      <footer className="timeline">
        <div>
          <span className="section-label">CONTINUOUS HISTORY</span>
          <strong>
            Timeline begins when the first authoritative world event is
            recorded.
          </strong>
        </div>
        <div className="timeline-line">
          <i />
          <span>FOUNDATION</span>
          <i />
          <span>WORLD GEN</span>
          <i />
          <span>FIRST NATIONS</span>
        </div>
        <button type="button" disabled>
          History unavailable
        </button>
      </footer>

      <nav className="mobile-nav" aria-label="Mobile primary navigation">
        {domains.slice(0, 4).map((item) => (
          <button
            key={item.label}
            type="button"
            className={activeDomain === item.label ? "active" : ""}
            onClick={() => setActiveDomain(item.label)}
          >
            <Glyph name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
        <button type="button" onClick={() => setBriefingOpen(true)}>
          <span className="more-icon">•••</span>
          <span>Briefing</span>
        </button>
      </nav>

      {noticesOpen && (
        <div
          className="scrim side-scrim"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setNoticesOpen(false)
          }
        >
          <section
            className="notice-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notices-title"
          >
            <div className="panel-heading">
              <h2 id="notices-title">Notifications</h2>
              <button
                type="button"
                aria-label="Close notifications"
                onClick={() => setNoticesOpen(false)}
              >
                ×
              </button>
            </div>
            <article>
              <span>PLATFORM</span>
              <h3>Application shell is now active</h3>
              <p>
                Navigation and briefing controls are available. This is not yet
                a playable universe.
              </p>
            </article>
            <article>
              <span>DEGRADED</span>
              <h3>Live world data unavailable</h3>
              <p>
                No world contract exists yet. Strategic mode remains available
                without fabricated data.
              </p>
            </article>
          </section>
        </div>
      )}
      {accountOpen && (
        <div
          className="scrim account-scrim"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setAccountOpen(false)
          }
        >
          <section
            className="account-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-title"
          >
            <div className="panel-heading">
              <div>
                <span className="section-label">IDENTITY NETWORK</span>
                <h2 id="account-title">
                  {authentication.status === "authenticated"
                    ? "Player session"
                    : "Enter PlanetConflict"}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close account panel"
                onClick={() => setAccountOpen(false)}
              >
                ×
              </button>
            </div>
            {authentication.status === "authenticated" &&
            authentication.session ? (
              <>
                <div className="identity-card">
                  <b>
                    {authentication.session.displayName ??
                      "Authenticated player"}
                  </b>
                  <span>
                    {authentication.session.email ?? "Email not shared"}
                  </span>
                  <small>
                    Firebase identity verified by PlanetConflict.Server
                  </small>
                </div>
                <button
                  className="google-signin secondary-action"
                  type="button"
                  onClick={() => void authentication.signOut()}
                >
                  Sign out of this tab
                </button>
              </>
            ) : (
              <>
                <p className="account-copy">
                  Use your Google account to establish an external identity.
                  Creating a nation and entering the permanent universe will be
                  separate, explicit steps.
                </p>
                <button
                  className="google-signin"
                  type="button"
                  onClick={() => void authentication.signIn()}
                  disabled={
                    authentication.status === "configuring" ||
                    authentication.status === "unconfigured"
                  }
                >
                  <span aria-hidden="true">G</span>
                  {authentication.status === "configuring"
                    ? "Connecting to Firebase…"
                    : "Continue with Google"}
                </button>
                {authentication.status === "unconfigured" && (
                  <p className="auth-message" role="status">
                    Firebase is not configured for this deployment. Add the
                    required Vite configuration and authorized domain before
                    enabling sign-in.
                  </p>
                )}
                {authentication.message && (
                  <p className="auth-message" role="alert">
                    {authentication.message}
                  </p>
                )}
                <p className="identity-notice">
                  PlanetConflict receives your Firebase user ID, display name,
                  and email. Social sign-in does not create a nation or grant
                  game permissions.
                </p>
              </>
            )}
          </section>
        </div>
      )}
      {paletteOpen && <CommandPalette onClose={closePalette} />}
    </div>
  );
}

function RouteLink({
  href,
  children,
  className,
}: {
  readonly href: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <a
      className={className}
      href={href}
      onClick={(event) => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        event.preventDefault();
        window.history.pushState({}, "", href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
    >
      {children}
    </a>
  );
}

function StatusPage({
  title,
  message,
  tone = "neutral",
  action,
}: {
  readonly title: string;
  readonly message: string;
  readonly tone?: string;
  readonly action?: React.ReactNode;
}) {
  return (
    <main className="route-state" id="main-content" tabIndex={-1}>
      <RouteLink href="/foundation" className="wordmark route-wordmark">
        <span className="brand-mark" aria-hidden="true">
          PC
        </span>
        <span>
          PLANET<em>CONFLICT</em>
        </span>
      </RouteLink>
      <section className={`route-card ${tone}`} aria-labelledby="route-title">
        <span className="section-label">COMMAND NETWORK</span>
        <h1 id="route-title">{title}</h1>
        <p>{message}</p>
        {action}
        <nav aria-label="Application routes">
          <RouteLink href="/foundation">Foundation</RouteLink>
          <RouteLink href="/catalog">Component catalog</RouteLink>
        </nav>
      </section>
    </main>
  );
}

function CommandRoute() {
  const authentication = useAuthentication();
  if (authentication.status === "authenticated") return <FoundationPage />;
  if (
    authentication.status === "configuring" ||
    authentication.status === "resolving-session"
  )
    return (
      <StatusPage
        title="Verifying command authority"
        message="Protected command data remains concealed while PlanetConflict.Server resolves your session."
        tone="loading"
      />
    );
  if (authentication.status === "denied")
    return (
      <StatusPage
        title="Command access denied"
        message={
          authentication.message ??
          "Your identity is valid but is not authorized for this command surface."
        }
        tone="denied"
      />
    );
  if (
    authentication.status === "unavailable" ||
    authentication.status === "error"
  )
    return (
      <StatusPage
        title="Command network unavailable"
        message={
          authentication.message ??
          "The authenticated session could not be resolved. No protected data was displayed."
        }
        tone="error"
      />
    );
  return (
    <StatusPage
      title="Sign in required"
      message="Authenticate with Google before entering the protected command surface. Signing in does not create a nation."
      action={
        <RouteLink href="/foundation" className="primary-action">
          Return to sign-in
        </RouteLink>
      }
    />
  );
}

const catalogRows = [
  [
    "Status badge",
    <>
      <span className="catalog-badge ready">◇ READY</span>{" "}
      <span className="catalog-badge stale">△ STALE</span>{" "}
      <span className="catalog-badge denied">⊘ DENIED</span>
    </>,
  ],
  [
    "Actions",
    <>
      <button className="primary-action" type="button">
        Primary command
      </button>{" "}
      <button className="secondary-action" type="button">
        Secondary
      </button>{" "}
      <button type="button" disabled>
        Unavailable
      </button>
    </>,
  ],
  [
    "Resource meter",
    <div className="resource-meter">
      <span>
        <b>ENERGY RESERVE</b>
        <small>62 / 100 GW</small>
      </span>
      <meter min="0" max="100" value="62">
        62%
      </meter>
    </div>,
  ],
  [
    "System alert",
    <div className="catalog-alert" role="status">
      <b>△ DEGRADED NETWORK</b>
      <span>
        Cached public shell available. Private projections remain unavailable.
      </span>
    </div>,
  ],
  [
    "Search field",
    <label className="catalog-field">
      Find an entity
      <input type="search" placeholder="Nation, region, or event" />
    </label>,
  ],
];

function CatalogPage() {
  return (
    <main className="catalog-page" id="main-content" tabIndex={-1}>
      <header className="catalog-header">
        <RouteLink href="/foundation" className="wordmark">
          <span className="brand-mark" aria-hidden="true">
            PC
          </span>
          <span>
            PLANET<em>CONFLICT</em>
          </span>
        </RouteLink>
        <div>
          <span className="section-label">DESIGN SYSTEM / GATE 2</span>
          <h1>Strategic interface catalog</h1>
          <p>
            Reusable, accessible primitives for dense permanent-world
            operations.
          </p>
        </div>
      </header>
      <nav className="catalog-tabs" aria-label="Catalog sections">
        <button className="selected" aria-pressed="true">
          Primitives
        </button>
        <button disabled>Domain editors</button>
        <button disabled>Live projections</button>
      </nav>
      <section className="catalog-grid" aria-label="Component examples">
        {catalogRows.map(([name, sample]) => (
          <article key={name as string}>
            <h2>{name}</h2>
            <div>{sample}</div>
          </article>
        ))}
      </section>
      <section className="catalog-wide" aria-labelledby="table-title">
        <h2 id="table-title">Strategic data table</h2>
        <div className="table-scroll">
          <table>
            <caption>Preview data, not authoritative world state</caption>
            <thead>
              <tr>
                <th scope="col">Region</th>
                <th scope="col">Scope</th>
                <th scope="col">Status</th>
                <th scope="col">Last event</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Survey frame 01</th>
                <td>Celestial body pending</td>
                <td>
                  <span className="catalog-badge stale">△ PREVIEW</span>
                </td>
                <td>Not recorded</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <section className="catalog-grid" aria-label="Application states">
        <article>
          <h2>Loading</h2>
          <div className="state-sample">
            <span className="spinner" aria-hidden="true" /> Resolving
            projection…
          </div>
        </article>
        <article>
          <h2>Empty</h2>
          <div className="state-sample">◇ No authoritative events</div>
        </article>
        <article>
          <h2>Error</h2>
          <div className="state-sample error">
            ! Connection failed <button type="button">Retry</button>
          </div>
        </article>
        <article>
          <h2>Consequence preview</h2>
          <div className="state-sample">
            <b>No state change</b>
            <span>
              This catalog action cannot issue an authoritative command.
            </span>
          </div>
        </article>
      </section>
    </main>
  );
}

function NotFoundPage() {
  return (
    <StatusPage
      title="Route not found"
      message="This location is not part of the current command network."
      action={
        <RouteLink href="/foundation" className="primary-action">
          Open foundation
        </RouteLink>
      }
    />
  );
}

const normalizePath = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;
const uuidPattern =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const worldRoutePattern = new RegExp(
  `^/worlds/(${uuidPattern})/bodies/(${uuidPattern})$`,
  "i",
);

export function App() {
  const [path, setPath] = useState(() =>
    normalizePath(window.location.pathname),
  );
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    if (path === "/") {
      window.history.replaceState({}, "", "/foundation");
      setPath("/foundation");
    }
  }, [path]);
  useEffect(() => {
    const update = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  useEffect(() => {
    const wentOnline = () => setOffline(false);
    const wentOffline = () => setOffline(true);
    const hasUpdate = () => setUpdateAvailable(true);
    window.addEventListener("online", wentOnline);
    window.addEventListener("offline", wentOffline);
    window.addEventListener("pc:shell-update", hasUpdate);
    return () => {
      window.removeEventListener("online", wentOnline);
      window.removeEventListener("offline", wentOffline);
      window.removeEventListener("pc:shell-update", hasUpdate);
    };
  }, []);
  useEffect(() => {
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>("main")?.focus(),
    );
    document.title =
      path === "/catalog"
        ? "Interface catalog · PlanetConflict"
        : path === "/command"
          ? "Command · PlanetConflict"
          : worldRoutePattern.test(path)
            ? "Strategic world survey · PlanetConflict"
            : "Foundation · PlanetConflict";
  }, [path]);
  let content: React.ReactNode;
  const worldMatch = worldRoutePattern.exec(path);
  if (path === "/foundation") content = <FoundationPage />;
  else if (path === "/command") content = <CommandRoute />;
  else if (path === "/catalog") content = <CatalogPage />;
  else if (worldMatch?.[1] && worldMatch[2])
    content = <WorldPage universeId={worldMatch[1]} bodyId={worldMatch[2]} />;
  else content = <NotFoundPage />;
  return (
    <>
      {(offline || updateAvailable) && (
        <div className="shell-notice" role="status">
          <b>
            {offline ? "OFFLINE STRATEGIC MODE" : "COMMAND SHELL UPDATE READY"}
          </b>
          <span>
            {offline
              ? "Public shell controls remain available; protected data is not cached."
              : "The update activates on your next navigation. Current work will not be interrupted."}
          </span>
        </div>
      )}
      {content}
    </>
  );
}
